import { API_BASE_URL } from "@/utils/getApiBase";
import { request } from '../request';

/**
 * Fetch user support profile with roles
 */
export function fetchSupportUserProfile() {
    return request<Api.Support.User>({
        url: `${API_BASE_URL}/api/support/user/profile/`,
        method: 'get'
    });
}

/**
 * Fetch tickets with optional filters and pagination
 * @param params Query parameters for filtering and pagination
 */
export function fetchTickets(params?: Api.Support.TicketQueryParams) {
    return request<Api.Support.PaginatedResponse<Api.Support.Ticket>>({
        url: `${API_BASE_URL}/api/support/tickets/`,
        method: 'get',
        params,
        metadata: {
            operationType: 'read',
            isUserInitiated: false,
            operationName: 'fetchTickets',
            silentFailure: true
        }
    } as any);
}

/**
* Create a new support ticket
* @param data Ticket creation data
*/
export function createTicket(data: Api.Support.CreateTicketData) {
    return request<Api.Support.Ticket>({
        url: `${API_BASE_URL}/api/support/tickets/`,
        method: 'post',
        data,
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'createTicket',
            showUserErrors: true
        }
    } as any);
}

/**
 * Fetch ticket details by ticket number
 * @param ticketNumber Ticket number (string identifier)
 */
export function fetchTicketDetails(ticketNumber: string) {
    return request<Api.Support.Ticket>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/`,
        method: 'get',
        metadata: {
            operationType: 'read',
            isUserInitiated: false,
            operationName: 'fetchTicketDetails',
            silentFailure: true
        }
    } as any);
}

/**
 * Update ticket details
 * @param ticketNumber Ticket number (string identifier)
 * @param data Updated ticket data
 */
export function updateTicket(ticketNumber: string, data: Partial<Api.Support.Ticket>) {
    return request<Api.Support.Ticket>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/`,
        method: 'patch',
        data,
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'updateTicket',
            showUserErrors: true
        }
    } as any);
}

/**
 * Assign ticket to an agent
 * @param ticketNumber Ticket number (string identifier)
 * @param agentId Agent user ID
 */
export function assignTicket(ticketNumber: string, agentId: number) {
    return request<Api.Support.Ticket>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/assign/`,
        method: 'post',
        data: { agent_id: agentId },
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'assignTicket',
            showUserErrors: true
        }
    } as any);
}

/**
* Change ticket status
* @param ticketNumber Ticket number (string identifier)
* @param status New status
*/
export function changeTicketStatus(ticketNumber: string, status: string) {
    return request<Api.Support.Ticket>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/change-status/`,
        method: 'post',
        data: { status },
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'changeTicketStatus',
            showUserErrors: true
        }
    } as any);
}

/**
* Change ticket priority
* @param ticketNumber Ticket number (string identifier)
* @param priority New priority
*/
export function changeTicketPriority(ticketNumber: string, priority: string) {
    return request<Api.Support.Ticket>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/change-priority/`,
        method: 'post',
        data: { priority },
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'changeTicketPriority',
            showUserErrors: true
        }
    } as any);
}

/**
 * Fetch ticket replies
 * @param ticketNumber Ticket number (string identifier)
 */
export function fetchTicketReplies(ticketNumber: string) {
    return request<Api.Support.TicketReply[]>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/replies/`,
        method: 'get'
    });
}

/**
 * Add reply to ticket
 * @param ticketNumber Ticket number (string identifier)
 * @param data Reply data
 */
export function addTicketReply(ticketNumber: string, data: Api.Support.CreateReplyData) {
    return request<Api.Support.TicketReply>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/replies/`,
        method: 'post',
        data,
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'addTicketReply',
            showUserErrors: true
        }
    } as any);
}

/**
 * Fetch ticket categories
 */
export function fetchTicketCategories() {
    return request<Api.Support.TicketCategory[]>({
        url: `${API_BASE_URL}/api/support/categories/`,
        method: 'get'
    });
}

/**
 * Fetch knowledge base categories
 * @param params Query parameters including language
 */
export function fetchKnowledgeBaseCategories(params?: {
    language?: string;
}) {
    return request<Api.Support.KnowledgeBaseCategory[]>({
        url: `${API_BASE_URL}/api/support/knowledge-base/categories/`,
        method: 'get',
        params
    });
}

/**
 * Fetch knowledge base articles
 * @param params Query parameters for filtering including language
 */
export function fetchKnowledgeBaseArticles(params?: {
    category?: number;
    search?: string;
    visibility?: 'public' | 'private';
    language?: string;
    ordering?: string;
    limit?: number;
}) {
    return request<Api.Support.KnowledgeBaseArticle[]>({
        url: `${API_BASE_URL}/api/support/knowledge-base/articles/`,
        method: 'get',
        params
    });
}

/**
 * Fetch a single knowledge base article by ID
 * @param id Article ID
 * @param params Query parameters including language
 */
export function fetchKnowledgeBaseArticle(id: number, params?: { language?: string }) {
    return request<Api.Support.KnowledgeBaseArticle>({
        url: `${API_BASE_URL}/api/support/knowledge-base/articles/${id}/`,
        method: 'get',
        params
    });
}

/**
 * Fetch popular knowledge base articles (sorted by view count)
 * @param params Query parameters including language and limit
 */
export function fetchPopularArticles(params?: {
    language?: string;
    limit?: number;
}) {
    return request<Api.Support.KnowledgeBaseArticle[]>({
        url: `${API_BASE_URL}/api/support/knowledge-base/articles/`,
        method: 'get',
        params: {
            ...params,
            ordering: '-view_count'
        }
    });
}

/**
 * Search knowledge base articles
 * @param query Search query
 * @param language Language code (e.g., 'fa-IR', 'en-US')
 */
export function searchKnowledgeBaseArticles(query: string, language?: string) {
    return request<{
        results: Api.Support.KnowledgeBaseArticle[];
        query: string;
        language: string;
        count: number;
    }>({
        url: `${API_BASE_URL}/api/v2/support/knowledge-base/search/`,
        method: 'get',
        params: { q: query, language }
    });
}

/**
 * Fetch knowledge base data from local JSON files (categories with articles)
 * @param language Language code (e.g., 'fa-IR', 'en-US')
 */
export function fetchLocalKnowledgeBaseData(language?: string) {
    return request<{
        categories: Array<{
            id: number;
            name: string;
            description: string;
            articles: Array<{
                id: number;
                title: string;
                slug: string;
                summary: string;
                url: string;
            }>;
        }>;
        language: string;
        total_categories: number;
    }>({
        url: `${API_BASE_URL}/api/v2/support/knowledge-base/`,
        method: 'get',
        params: { language }
    });
}

/**
 * Mark knowledge base article as helpful
 * @param id Article ID
 */
export function markArticleHelpful(id: number) {
    return request<Api.Support.KnowledgeBaseArticle>({
        url: `${API_BASE_URL}/api/support/knowledge-base/articles/${id}/mark_helpful/`,
        method: 'post'
    });
}

/**
 * Mark knowledge base article as not helpful
 * @param id Article ID
 */
export function markArticleNotHelpful(id: number) {
    return request<Api.Support.KnowledgeBaseArticle>({
        url: `${API_BASE_URL}/api/support/knowledge-base/articles/${id}/mark_not_helpful/`,
        method: 'post'
    });
}

/**
 * Get file upload information (allowed types and limits)
 */
export function fetchFileUploadInfo() {
    return request<Api.Support.FileUploadInfo>({
        url: `${API_BASE_URL}/api/support/file-upload/`,
        method: 'get'
    });
}

/**
 * Upload support file and get presigned URL
 * @param data File upload data
 */
export function uploadSupportFile(data: {
    file_name: string;
    file_type: string;
    file_size: number;
    ticket_number?: string;
}) {
    return request<Api.Support.FileUploadResponse>({
        url: `${API_BASE_URL}/api/support/file-upload/`,
        method: 'post',
        data
    });
}

/**
 * Fetch support agents list (admin/agent only)
 */
export function fetchSupportAgents() {
    return request<Api.Support.User[]>({
        url: `${API_BASE_URL}/api/support/roles/agents/`,
        method: 'get'
    });
}

/**
 * Fetch support admins list (admin only)
 */
export function fetchSupportAdmins() {
    return request<Api.Support.User[]>({
        url: `${API_BASE_URL}/api/support/roles/admins/`,
        method: 'get'
    });
}

/**
 * Create support role (admin only)
 * @param data Role creation data
 */
export function createSupportRole(data: {
    user_id: number;
    role: 'agent' | 'admin';
}) {
    return request<Api.Support.SupportRole>({
        url: `${API_BASE_URL}/api/support/roles/`,
        method: 'post',
        data
    });
}

/**
 * Fetch canned responses (agent/admin only)
 */
export function fetchCannedResponses() {
    return request<Api.Support.CannedResponse[]>({
        url: `${API_BASE_URL}/api/support/canned-responses/`,
        method: 'get'
    });
}

/**
 * Use canned response (agent/admin only)
 * @param id Canned response ID
 */
export function useCannedResponse(id: number) {
    return request<Api.Support.CannedResponse>({
        url: `${API_BASE_URL}/api/support/canned-responses/${id}/use/`,
        method: 'post'
    });
}

/**
 * Fetch ticket templates (agent/admin only)
 */
export function fetchTicketTemplates() {
    return request<Api.Support.TicketTemplate[]>({
        url: `${API_BASE_URL}/api/support/ticket-templates/`,
        method: 'get'
    });
}

/**
 * Fetch support analytics (admin only)
 */
export function fetchSupportAnalytics() {
    return request<Api.Support.SupportAnalytics>({
        url: `${API_BASE_URL}/api/support/analytics/`,
        method: 'get'
    });
}

/**
 * Fetch dashboard statistics for current user
 */
export function fetchDashboardStats() {
    return request<Api.Support.DashboardStats>({
        url: `${API_BASE_URL}/api/support/dashboard/stats/`,
        method: 'get',
        metadata: {
            operationType: 'read',
            isUserInitiated: false,
            operationName: 'fetchDashboardStats',
            silentFailure: true
        }
    } as any);
}

/**
 * Search tickets with query
 * @param query Search query
 * @param filters Additional filters
 */
export function searchTickets(query: string, filters?: Api.Support.TicketFilters) {
    return request<Api.Support.PaginatedResponse<Api.Support.Ticket>>({
        url: `${API_BASE_URL}/api/support/tickets/search/`,
        method: 'get',
        params: {
            q: query,
            ...filters
        }
    });
}

/**
 * Close ticket
 * @param ticketNumber Ticket number (string identifier)
 */
export function closeTicket(ticketNumber: string) {
    return request<Api.Support.Ticket>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/close/`,
        method: 'post',
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'closeTicket',
            showUserErrors: true
        }
    } as any);
}

/**
 * Reopen ticket
 * @param ticketNumber Ticket number (string identifier)
 */
export function reopenTicket(ticketNumber: string) {
    return request<Api.Support.Ticket>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/reopen/`,
        method: 'post',
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'reopenTicket',
            showUserErrors: true
        }
    } as any);
}

/**
 * Delete ticket attachment
 * @param ticketNumber Ticket number (string identifier)
 * @param attachmentId Attachment ID
 */
export function deleteTicketAttachment(ticketNumber: string, attachmentId: number) {
    return request<{ success: boolean }>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/attachments/${attachmentId}/`,
        method: 'delete',
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'deleteTicketAttachment',
            showUserErrors: true
        }
    } as any);
}

/**
 * Fetch ticket activity/timeline
 * @param ticketNumber Ticket number (string identifier)
 * @param params Optional parameters for filtering and pagination
 */
export function fetchTicketActivity(ticketNumber: string, params?: {
    include_internal?: boolean;
    limit?: number;
    offset?: number;
}) {
    return request<Api.Support.PaginatedActivityResponse>({
        url: `${API_BASE_URL}/api/support/tickets/${ticketNumber}/activity/`,
        method: 'get',
        params,
        metadata: {
            operationType: 'read',
            isUserInitiated: false,
            operationName: 'fetchTicketActivity',
            silentFailure: true
        }
    } as any);
}

// ==================== BULK ACTIONS ====================

/**
 * Bulk assign tickets to an agent
 * @param ticketNumbers Array of ticket numbers
 * @param agentId Agent user ID (null to unassign)
 */
export function bulkAssignTickets(ticketNumbers: string[], agentId: number | null) {
    return request<Api.Support.BulkActionResponse>({
        url: `${API_BASE_URL}/api/support/tickets/bulk-assign/`,
        method: 'post',
        data: {
            ticket_numbers: ticketNumbers,
            agent_id: agentId
        },
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'bulkAssignTickets',
            showUserErrors: true
        }
    } as any);
}

/**
 * Bulk change ticket status
 * @param ticketNumbers Array of ticket numbers
 * @param status New status
 */
export function bulkChangeStatus(ticketNumbers: string[], status: Api.Support.Ticket['status']) {
    return request<Api.Support.BulkActionResponse>({
        url: `${API_BASE_URL}/api/support/tickets/bulk-status/`,
        method: 'post',
        data: {
            ticket_numbers: ticketNumbers,
            status
        },
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'bulkChangeStatus',
            showUserErrors: true
        }
    } as any);
}

/**
 * Bulk change ticket priority
 * @param ticketNumbers Array of ticket numbers
 * @param priority New priority
 */
export function bulkChangePriority(ticketNumbers: string[], priority: Api.Support.Ticket['priority']) {
    return request<Api.Support.BulkActionResponse>({
        url: `${API_BASE_URL}/api/support/tickets/bulk-priority/`,
        method: 'post',
        data: {
            ticket_numbers: ticketNumbers,
            priority
        },
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'bulkChangePriority',
            showUserErrors: true
        }
    } as any);
}

// ==================== EXPORT ====================

/**
 * Export tickets to CSV or Excel
 * @param params Export parameters and filters
 * @returns Blob response for file download
 */
export function exportTickets(params?: Api.Support.ExportParams) {
    return request<Blob>({
        url: `${API_BASE_URL}/api/support/tickets/export/`,
        method: 'get',
        params,
        responseType: 'blob',
        metadata: {
            operationType: 'read',
            isUserInitiated: true,
            operationName: 'exportTickets',
            showUserErrors: true
        }
    } as any);
}

// ==================== NOTIFICATION PREFERENCES ====================

/**
 * Fetch notification preferences for the current user
 */
export async function fetchNotificationPreferences() {
    const result = await request<Api.Support.NotificationPreferencesResponse>({
        url: `${API_BASE_URL}/api/support/notification-preferences/`,
        method: 'get',
        metadata: {
            operationType: 'read',
            isUserInitiated: false,
            operationName: 'fetchNotificationPreferences',
            silentFailure: true
        }
    } as any);
    
    // Extract preferences from response wrapper
    if (result.data?.preferences) {
        return { data: result.data.preferences, error: result.error };
    }
    return { data: null, error: result.error };
}

/**
 * Update notification preferences
 * @param data Partial notification preferences data
 */
export async function updateNotificationPreferences(data: Api.Support.UpdateNotificationPreferencesData) {
    const result = await request<Api.Support.NotificationPreferencesResponse>({
        url: `${API_BASE_URL}/api/support/notification-preferences/update/`,
        method: 'patch',
        data,
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'updateNotificationPreferences',
            showUserErrors: true
        }
    } as any);
    
    // Extract preferences from response wrapper
    if (result.data?.preferences) {
        return { data: result.data.preferences, error: result.error };
    }
    return { data: null, error: result.error };
}

/**
 * Reset notification preferences to defaults
 */
export async function resetNotificationPreferences() {
    const result = await request<Api.Support.NotificationPreferencesResponse>({
        url: `${API_BASE_URL}/api/support/notification-preferences/reset/`,
        method: 'post',
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'resetNotificationPreferences',
            showUserErrors: true
        }
    } as any);
    
    // Extract preferences from response wrapper
    if (result.data?.preferences) {
        return { data: result.data.preferences, error: result.error };
    }
    return { data: null, error: result.error };
}

/**
 * Toggle all notifications on or off
 * @param enabled Whether to enable or disable all notifications
 */
export async function toggleAllNotifications(enabled: boolean) {
    const result = await request<Api.Support.NotificationPreferencesResponse>({
        url: `${API_BASE_URL}/api/support/notification-preferences/toggle/`,
        method: 'post',
        data: { enabled },
        metadata: {
            operationType: 'write',
            isUserInitiated: true,
            operationName: 'toggleAllNotifications',
            showUserErrors: true
        }
    } as any);
    
    // Extract preferences from response wrapper
    if (result.data?.preferences) {
        return { data: result.data.preferences, error: result.error };
    }
    return { data: null, error: result.error };
}